import { put, select, all, takeEvery, call } from 'redux-saga/effects';
import { ActionType, createReducer } from 'typesafe-actions';
import { setCookie, getCookie, serializeAEMCookieCollection, deserializeAEMCookieCollection } from 'utils';
import { getDomainSelector } from 'common/selectors';
import { EffectR } from 'types/utils';
import { WishlistItem } from '../types';
import { getWishlist, getWishlistItem } from './selectors';
import { WISHLIST_SET, WISHLIST_UPDATE_ITEM, WISHLIST_RESET, WISHLIST_ITEM_FLOW } from './actions';

const COOKIE_KEY = 'shoppingList';

const serializeWishlist = serializeAEMCookieCollection(
  (item: WishlistItem) => `${item.code}:${item.quantity}`,
);
const deserializeWishlist = deserializeAEMCookieCollection<WishlistItem>((item: string) => {
  const [code, qty] = item.split(':');
  return {
    code,
    quantity: parseFloat(qty),
  };
});

export const wishlist = createReducer<WishlistItem[] | null>([])
  .handleAction(WISHLIST_SET, (state, { payload }) => payload)
  .handleAction(WISHLIST_UPDATE_ITEM, (state, { payload }) => {
    const res = state instanceof Array ? [...state] : [];

    const itemIdx = res.findIndex(({ code }) => code === payload.code);
    if (itemIdx >= 0) {
      res.splice(itemIdx, 1);
    }

    if (payload.quantity > 0) {
      res.push(payload);
    }

    return res;
  });

export function* resetWishlist() {
  const wishlistItems: WishlistItem[] = deserializeWishlist(getCookie(COOKIE_KEY) || '');
  yield put(WISHLIST_SET(wishlistItems));
}

export function* updateWishlistItem() {
  const wishlistItems: WishlistItem[] = yield select(getWishlist);
  const domain = (yield select(getDomainSelector)) as EffectR.Select<typeof getDomainSelector>;

  setCookie(COOKIE_KEY, serializeWishlist(wishlistItems), {
    path: '/',
    domain,
    expires: new Date(new Date().getFullYear() + 1, 12, 31),
  });
  document.body.dispatchEvent(
    new CustomEvent('wishlist:update', {
      cancelable: true,
      bubbles: true,
      composed: true,
    }),
  );
}

export function* rootSaga() {
  yield all([
    takeEvery(WISHLIST_RESET, resetWishlist),
    takeEvery(WISHLIST_UPDATE_ITEM, updateWishlistItem),
    takeEvery(WISHLIST_ITEM_FLOW, handleWishlistItemFlow),
  ]);
}

export function* handleWishlistItemFlow({ payload }: ActionType<typeof WISHLIST_ITEM_FLOW>) {
  const { productId, flowQty } = payload;
  const itemQty =
    ((yield call(selectWishlistItemQty, productId)) as EffectR.Call<typeof selectWishlistItemQty>) ?? 0;
  yield put(
    WISHLIST_UPDATE_ITEM({
      code: productId,
      quantity: itemQty + flowQty,
    }),
  );
}

export function* selectWishlistItemQty(productId: string) {
  const wli = (yield select(getWishlistItem(productId))) as EffectR.Select<
    ReturnType<typeof getWishlistItem>
  >;
  return wli?.quantity;
}
